// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { Button, InputBox } from '@imprivata-cloud/components';
import { Divider, Form } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Appearance.less';
import { UndoOutlined } from '@ant-design/icons';
import { SystemType } from '../../../api/constants';
import { orgPreferencesGet } from '../../../api/portalServices';
import type { OrgPreferencesGetResponse } from '../../../api/types';
import { withRole } from '../../../common/authorization/Roles';
import { LegalFileUpload } from '../../../components/legalFileUpload/LegalFileUpload';
import LogoUpload from '../../../components/logoUpload/LogoUpload';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '../../../errorHandler/errors';
import useOrganizationInfoSave from '../../../setup/hooks/useOrganizationInfoSave';
import { ROLE_TYPE_ADMIN } from '../../constants';

enum FieldNames {
  LOGO = 'logo',
  ORG_NAME = 'orgName',
}

interface FormValues {
  [FieldNames.ORG_NAME]: string;
  [FieldNames.LOGO]: File;
}

const TRANSLATION_ROOT = 'portal.appearance';

function Appearance() {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const organizationInfoSave = useOrganizationInfoSave(SystemType.PORTAL);
  const orgPreferencesGetResponse = useRef<OrgPreferencesGetResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      clearErrorNotifications();
      setIsLoading(true);
      const orgPreferences = await orgPreferencesGet();
      form.setFieldValue(FieldNames.ORG_NAME, orgPreferences.orgName);
      form.setFieldValue([FieldNames.LOGO], undefined);
      orgPreferencesGetResponse.current = orgPreferences;
      setIsFormDirty(false);
      setIsLoading(false);
      // this will clear errors in the form if any left from pre-fetch user changes
      await form.validateFields();
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
  }, [form.setFieldValue, emitError, clearErrorNotifications, form.validateFields]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  async function onFinish(formValues: FormValues) {
    if (!orgPreferencesGetResponse.current?.orgName) {
      emitError(AppError.fromMessage('Invalid state exception: orgName cannot be empty.'));
      return;
    }
    if (!isFormDirty) {
      return;
    }
    try {
      setIsLoading(true);
      clearErrorNotifications();
      await organizationInfoSave.mutateAsync({
        orgName: formValues[FieldNames.ORG_NAME],
        usernameLabel: orgPreferencesGetResponse.current.usernameLabel,
        passwordLabel: orgPreferencesGetResponse.current.passwordLabel,
        proxCardLabel: orgPreferencesGetResponse.current.proxCardLabel,
        logoFile: formValues[FieldNames.LOGO],
        oldLogoUrl: orgPreferencesGetResponse.current?.logoUrl,
      });
      await fetchData();
    } catch (e) {
      emitError(new AppError(e as ApiError));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={'appearance-container'} aria-description="appearance-form">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={() => setIsFormDirty(true)}
        className="appearance-form"
      >
        <Form.Item name={FieldNames.ORG_NAME} rules={[{ required: true }]}>
          <InputBox label={t(`${TRANSLATION_ROOT}.org-name-label`)} autoFocus />
        </Form.Item>

        <LogoUpload
          isLoading={isLoading}
          oldImageUrl={orgPreferencesGetResponse.current?.logoUrl}
          fieldName={FieldNames.LOGO}
          fieldValue={form.getFieldValue(FieldNames.LOGO)}
        />

        <div className="btns-container">
          <Button
            type="text"
            className="text-button"
            onClick={fetchData}
            disabled={!isFormDirty}
            icon={<UndoOutlined className="undo-icon" />}
          >
            {t('common.undo')}
          </Button>
          <Button type="primary" htmlType="submit" disabled={isLoading || !isFormDirty}>
            {t('common.save')}
          </Button>
        </div>
      </Form>
      <div className="divider-container">
        <Divider className="divider" />
      </div>
      <LegalFileUpload />
    </div>
  );
}

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(Appearance);
